import { Injectable } from '@angular/core';
import { BridgeCommandsService } from '../../../bridge-commands.service';
import {
  asReffable,
  DocumentReference,
  Firestore,
  WithRef,
} from '@principle-theorem/shared';
import {
  BridgeDeviceStatus,
  IBridgeDevice,
  IPatient,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  DentalEyeAddPatientRecordCommand,
  DentalEyeOpenPatientCommand,
  DentalEyeToDeviceCommand,
} from '@principle-theorem/principle-bridge-core';
import { Practice } from '@principle-theorem/principle-core';
import {
  ConfirmDialogComponent,
  DialogPresets,
  IConfirmationDialogInput,
  confirmationDialogData,
} from '@principle-theorem/ng-shared';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class DentalEyeFeatureService {
  constructor(
    private _bridgeCommands: BridgeCommandsService,
    private _dialog: MatDialog
  ) {}

  async openPatient(
    patient: WithRef<IPatient>,
    deviceRef: DocumentReference<IBridgeDevice>
  ): Promise<void> {
    const practiceRef = Firestore.getParentDocRef<IPractice>(deviceRef);
    const bridgeDevices = await Firestore.getDocs(
      Practice.bridgeDeviceCol(asReffable(practiceRef))
    );
    const masterDevice = bridgeDevices.find(
      (device) =>
        device.status === BridgeDeviceStatus.Active &&
        !!device.settings?.dentalEye?.enabled &&
        !!device.settings.dentalEye.isMaster
    );

    if (!masterDevice) {
      const data = confirmationDialogData({
        title: 'No Master Bridge Device',
        prompt: [
          'The DentalEye master bridge device is offline or not set up. Uploads from DentalEye to Principle will not work until the master bridge is available.',
          'Continue with opening the patient in DentalEye?',
        ],
        submitLabel: 'Open Patient',
      });
      const confirmed = await this._dialog
        .open<ConfirmDialogComponent, IConfirmationDialogInput, boolean>(
          ConfirmDialogComponent,
          DialogPresets.small({ data })
        )
        .afterClosed()
        .toPromise();

      if (!confirmed) {
        return;
      }
    } else {
      const addPatientRecordCommand: DentalEyeAddPatientRecordCommand = {
        type: DentalEyeToDeviceCommand.AddPatientRecord,
        data: {
          patient,
        },
      };
      await this._bridgeCommands.sendCommand(
        addPatientRecordCommand,
        masterDevice.ref
      );
    }

    const openPatientCommand: DentalEyeOpenPatientCommand = {
      type: DentalEyeToDeviceCommand.OpenPatient,
      data: {
        patient,
      },
    };
    await this._bridgeCommands.sendCommand(openPatientCommand, deviceRef);
  }
}
